/**
 * Game Comparison Component
 * 
 * Main component for comparing multiple pull tab games side-by-side.
 * Provides detailed analysis including odds, payout structures, and
 * comparative rankings to help users make informed decisions.
 * 
 * Features:
 * - Side-by-side game comparison view
 * - Metric rankings and highlights
 * - Sortable comparison results
 * - Responsive design for mobile and desktop
 * - Interactive metric explanations
 * 
 * @module TabPicker.GameComparison
 */

import React, { useEffect, useState } from 'react';
import { useComparisonStore, ComparisonResults } from '../stores/comparisonStore';
import { useGetApiGames } from '../api/generated/games/games';
import { performGameComparison } from '../utils/comparisonCalculations';
import { JarFillImpactPanel } from './JarFillImpactPanel';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Table } from './ui/Table';
import { Modal } from './ui/Modal';

/**
 * Format currency value for display
 */
const formatCurrency = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

/**
 * Format percentage value for display
 */
const formatPercentage = (value: number, decimals: number = 2): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Get rank badge variant based on rank position
 */
const getRankBadgeVariant = (rank: number): 'success' | 'warning' | 'default' => {
  if (rank === 1) return 'success';
  if (rank === 2) return 'warning';
  return 'default';
};

/**
 * Get metric trend indicator
 */
const getMetricTrend = (value: number, rank: number, isReverse: boolean = false): string => {
  if (rank === 1) return isReverse ? '🔽' : '🔺';
  if (rank === 2) return '➡️';
  return isReverse ? '🔺' : '🔽';
};

/**
 * Calculate adjusted metrics based on deal status
 */
const calculateAdjustedMetrics = (game: ComparisonResults['games'][0], dealStatus?: { jarFillPercentage: number }) => {
  if (!dealStatus || !game.comparisonMetrics) {
    return {
      adjustedExpectedValue: game.comparisonMetrics?.expectedValue || 0,
      adjustedWinProbability: game.comparisonMetrics?.winProbability || 0,
      adjustedHoldPercentage: game.comparisonMetrics?.holdPercentage || 0,
      hasAdjustment: false,
    };
  }

  // Simple proportional adjustment based on jar fill percentage
  // This is a simplified calculation - in a real implementation, you'd want more sophisticated logic
  const fillFactor = dealStatus.jarFillPercentage / 100;
  const originalExpectedValue = game.comparisonMetrics.expectedValue;
  const originalWinProbability = game.comparisonMetrics.winProbability;
  const originalHoldPercentage = game.comparisonMetrics.holdPercentage;

  // As jar empties, expected value typically decreases (high-value prizes claimed first)
  // This is a simplified model - actual calculation would depend on specific prize structure
  const adjustmentFactor = 0.3 + (0.7 * fillFactor); // More dramatic adjustment for demonstration
  
  return {
    adjustedExpectedValue: originalExpectedValue * adjustmentFactor,
    adjustedWinProbability: originalWinProbability * (0.5 + 0.5 * fillFactor),
    adjustedHoldPercentage: originalHoldPercentage + (10 * (1 - fillFactor)), // Hold percentage increases as jar empties
    hasAdjustment: true,
  };
};

/**
 * Dual metrics display component
 */
const DualMetricDisplay: React.FC<{
  label: string;
  originalValue: number;
  adjustedValue?: number;
  rank: number;
  formatter: (value: number) => string;
  showOriginalOdds: boolean;
}> = ({ label, originalValue, adjustedValue, rank, formatter, showOriginalOdds }) => {
  const hasAdjustment = adjustedValue !== undefined && adjustedValue !== originalValue;
  
  return (
    <div className="flex items-center justify-between">
      <span className="text-base tablet:text-sm text-gray-600">{label}</span>
      <div className="flex items-center gap-2">
        <Badge variant={getRankBadgeVariant(rank)} size="sm">
          #{rank}
        </Badge>
        <div className="text-right">
          {hasAdjustment && (
            <div className="text-sm tablet:text-xs text-green-600 font-medium">
              Live: {formatter(adjustedValue!)}
            </div>
          )}
          <div className={`font-semibold ${hasAdjustment && showOriginalOdds ? 'text-sm tablet:text-xs text-gray-500' : ''}`}>
            {hasAdjustment && showOriginalOdds && 'Printed: '}{formatter(originalValue)}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Comparison metrics summary card
 */
const MetricsSummaryCard: React.FC<{ results: ComparisonResults }> = ({ results }) => {
  const { summary } = results;
  
  // Find game names for summary
  const getBestGameName = (gameId: number): string => {
    const game = results.games.find(g => g.id === gameId);
    return game?.name || `Game ${gameId}`;
  };

  return (
    <Card className="mb-6">
      <div className="p-6">
        <h3 className="text-xl tablet:text-lg font-semibold mb-4">Comparison Summary</h3>
        <div className="grid tablet:grid-cols-2 desktop:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{summary.totalGames}</div>
            <div className="text-base tablet:text-sm text-gray-600">Games Compared</div>
          </div>
          
          <div className="text-center">
            <div className="text-base tablet:text-sm font-medium text-green-700 truncate">
              {getBestGameName(summary.bestOverall)}
            </div>
            <div className="text-base tablet:text-sm text-gray-600">Best Overall</div>
          </div>
          
          <div className="text-center">
            <div className="text-base tablet:text-sm font-medium text-purple-700 truncate">
              {getBestGameName(summary.highestExpectedValue)}
            </div>
            <div className="text-base tablet:text-sm text-gray-600">Highest Expected Value</div>
          </div>
          
          <div className="text-center">
            <div className="text-base tablet:text-sm font-medium text-orange-700 truncate">
              {getBestGameName(summary.lowestHoldPercentage)}
            </div>
            <div className="text-base tablet:text-sm text-gray-600">Lowest Hold %</div>
          </div>
        </div>
      </div>
    </Card>
  );
};

/**
 * Individual game comparison card
 */
const GameComparisonCard: React.FC<{
  game: ComparisonResults['games'][0];
  isFirst: boolean;
}> = ({ game, isFirst }) => {
  const { comparisonMetrics, statistics } = game;
  const { config } = useComparisonStore();
  
  if (!comparisonMetrics) return null;

  // Get deal status for this game
  const dealStatus = config.dealStatuses?.find(ds => ds.gameId === game.id);
  const adjustedMetrics = calculateAdjustedMetrics(game, dealStatus);

  return (
    <Card className={`h-full ${isFirst ? 'ring-2 ring-green-500' : ''}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl tablet:text-lg font-semibold truncate">{game.name}</h3>
          <div className="flex items-center gap-2">
            {dealStatus && (
              <Badge variant="warning" size="sm">
                {dealStatus.jarFillPercentage}% filled
              </Badge>
            )}
            {isFirst && (
              <Badge variant="success" size="sm">
                Best
              </Badge>
            )}
          </div>
        </div>
        
        <div className="space-y-3">
          {/* Expected Value */}
          <DualMetricDisplay
            label="Expected Value"
            originalValue={comparisonMetrics.expectedValue}
            adjustedValue={adjustedMetrics.hasAdjustment ? adjustedMetrics.adjustedExpectedValue : undefined}
            rank={comparisonMetrics.expectedValueRank}
            formatter={formatCurrency}
            showOriginalOdds={config.showOriginalOdds}
          />
          
          {/* Win Probability */}
          <DualMetricDisplay
            label="Win Probability"
            originalValue={comparisonMetrics.winProbability}
            adjustedValue={adjustedMetrics.hasAdjustment ? adjustedMetrics.adjustedWinProbability : undefined}
            rank={comparisonMetrics.winProbabilityRank}
            formatter={formatPercentage}
            showOriginalOdds={config.showOriginalOdds}
          />
          
          {/* Hold Percentage */}
          <DualMetricDisplay
            label="Hold Percentage"
            originalValue={comparisonMetrics.holdPercentage}
            adjustedValue={adjustedMetrics.hasAdjustment ? adjustedMetrics.adjustedHoldPercentage : undefined}
            rank={comparisonMetrics.holdPercentageRank}
            formatter={(value) => `${value.toFixed(2)}%`}
            showOriginalOdds={config.showOriginalOdds}
          />
          
          {/* Efficiency Score */}
          <DualMetricDisplay
            label="Efficiency"
            originalValue={comparisonMetrics.efficiency}
            adjustedValue={undefined} // Keep efficiency as original for now
            rank={comparisonMetrics.efficiencyRank}
            formatter={(value) => `${value.toFixed(1)}/100`}
            showOriginalOdds={config.showOriginalOdds}
          />
          
          {/* Risk Score */}
          <DualMetricDisplay
            label="Risk Score"
            originalValue={comparisonMetrics.riskScore}
            adjustedValue={undefined} // Keep risk score as original for now
            rank={comparisonMetrics.riskScoreRank}
            formatter={(value) => `${value.toFixed(1)}/100`}
            showOriginalOdds={config.showOriginalOdds}
          />
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-sm tablet:text-xs text-gray-500 space-y-1">
            <div>Ticket Price: {formatCurrency(game.ticketPrice)}</div>
            <div>Total Tickets: {game.totalTickets?.toLocaleString() || 'N/A'}</div>
            <div>Max Prize: {formatCurrency(comparisonMetrics.maxPrize)}</div>
          </div>
        </div>
        
        {/* Deal Status Notice */}
        {dealStatus && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-base tablet:text-sm">
              <div className="text-green-600">🎯</div>
              <div className="text-green-700">
                <strong>Live Odds Active:</strong> Metrics adjusted for {dealStatus.jarFillPercentage}% jar fill
              </div>
            </div>
          </div>
        )}
        
        {!dealStatus && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-base tablet:text-sm text-blue-700">
              💡 <strong>Tip:</strong> Adjust jar fill percentages above to see real-time odds updates
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

/**
 * Detailed comparison table
 */
const DetailedComparisonTable: React.FC<{ results: ComparisonResults }> = ({ results }) => {
  return (
    <div className="mt-8">
      <h3 className="text-xl tablet:text-lg font-semibold mb-4">Detailed Comparison</h3>
      <div className="overflow-x-auto">
        <Table responsive hover>
          <thead>
            <tr>
              <th className="px-4 py-2 text-left">Game Name</th>
              <th className="px-4 py-2 text-left">Expected Value</th>
              <th className="px-4 py-2 text-left">Win Probability</th>
              <th className="px-4 py-2 text-left">Hold %</th>
              <th className="px-4 py-2 text-left">Efficiency</th>
              <th className="px-4 py-2 text-left">Risk Score</th>
            </tr>
          </thead>
          <tbody>
            {results.games.map((game) => (
              <tr key={game.id}>
                <td className="px-4 py-2 font-medium">{game.name}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={getRankBadgeVariant(game.comparisonMetrics?.expectedValueRank || 1)} size="sm">
                      #{game.comparisonMetrics?.expectedValueRank}
                    </Badge>
                    {formatCurrency(game.comparisonMetrics?.expectedValue || 0)}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={getRankBadgeVariant(game.comparisonMetrics?.winProbabilityRank || 1)} size="sm">
                      #{game.comparisonMetrics?.winProbabilityRank}
                    </Badge>
                    {formatPercentage(game.comparisonMetrics?.winProbability || 0)}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={getRankBadgeVariant(game.comparisonMetrics?.holdPercentageRank || 1)} size="sm">
                      #{game.comparisonMetrics?.holdPercentageRank}
                    </Badge>
                    {(game.comparisonMetrics?.holdPercentage || 0).toFixed(2)}%
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={getRankBadgeVariant(game.comparisonMetrics?.efficiencyRank || 1)} size="sm">
                      #{game.comparisonMetrics?.efficiencyRank}
                    </Badge>
                    {(game.comparisonMetrics?.efficiency || 0).toFixed(1)}/100
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={getRankBadgeVariant(game.comparisonMetrics?.riskScoreRank || 1)} size="sm">
                      #{game.comparisonMetrics?.riskScoreRank}
                    </Badge>
                    {(game.comparisonMetrics?.riskScore || 0).toFixed(1)}/100
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

/**
 * Main Game Comparison Component
 */
export const GameComparison: React.FC = () => {
  const {
    results,
    isLoading,
    error,
    selectedGameIds,
    config,
    canCompare,
    setSortBy,
    setSortOrder,
    setShowOriginalOdds,
    setResults,
    setLoading,
    setError,
    clearResults,
  } = useComparisonStore();

  const [showDetailedView, setShowDetailedView] = useState(false);
  
  // Get games data from API
  const { data: gamesData } = useGetApiGames();

  // Real comparison function using our calculation utilities
  const performComparisonCalculation = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Performing comparison for games:', selectedGameIds);
      console.log('Config:', config);
      
      // Get the selected games from the API data
      if (!gamesData?.games) {
        throw new Error('No games data available');
      }
      
      const selectedGames = gamesData.games.filter(game => 
        selectedGameIds.includes(game.id)
      );
      
      if (selectedGames.length < 2) {
        throw new Error('At least 2 games must be selected for comparison');
      }
      
      // Add a small delay for better UX (shows loading state)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Perform the comparison calculation with deal status
      const comparisonResults = performGameComparison(selectedGames, {
        dealStatuses: config.dealStatuses || [],
        showOriginalOdds: config.showOriginalOdds || false,
      });
      
      // Apply sorting based on config
      const sortedGames = [...comparisonResults.games].sort((a, b) => {
        let aValue: number;
        let bValue: number;
        
        switch (config.sortBy) {
          case 'expectedValue':
            aValue = a.comparisonMetrics?.expectedValue || 0;
            bValue = b.comparisonMetrics?.expectedValue || 0;
            break;
          case 'winProbability':
            aValue = a.comparisonMetrics?.winProbability || 0;
            bValue = b.comparisonMetrics?.winProbability || 0;
            break;
          case 'holdPercentage':
            aValue = a.comparisonMetrics?.holdPercentage || 0;
            bValue = b.comparisonMetrics?.holdPercentage || 0;
            break;
          case 'name':
            return config.sortOrder === 'asc' 
              ? a.name.localeCompare(b.name)
              : b.name.localeCompare(a.name);
          default:
            aValue = a.comparisonMetrics?.expectedValue || 0;
            bValue = b.comparisonMetrics?.expectedValue || 0;
        }
        
        return config.sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      });
      
      const sortedResults = {
        ...comparisonResults,
        games: sortedGames,
      };
      
      setResults(sortedResults);
      
    } catch (error) {
      console.error('Comparison failed:', error);
      setError(error instanceof Error ? error.message : 'Comparison failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!results && selectedGameIds.length < 2) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-4xl mb-4">📊</div>
        <h3 className="text-xl tablet:text-lg font-semibold text-gray-700 mb-2">
          Select Games to Compare
        </h3>
        <p className="text-gray-500 mb-4">
          Choose at least 2 games to see detailed comparison analysis
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Analyzing games...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 text-4xl mb-4">⚠️</div>
        <h3 className="text-xl tablet:text-lg font-semibold text-red-700 mb-2">
          Comparison Failed
        </h3>
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => performComparisonCalculation()} variant="secondary">
          Try Again
        </Button>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="text-center py-12">
        <h3 className="text-xl tablet:text-lg font-semibold text-gray-700 mb-4">
          Ready to Compare {selectedGameIds.length} Games
        </h3>
        <div className="flex gap-4 justify-center">
          <Button
            onClick={performComparisonCalculation}
            disabled={!canCompare()}
            className="px-6"
          >
            Compare Games
          </Button>
          <Button
            onClick={clearResults}
            variant="secondary"
          >
            Clear Selection
          </Button>
        </div>
      </div>
    );
  }

  // Check if any deal statuses are active
  const hasActiveDealStatuses = config.dealStatuses && config.dealStatuses.length > 0;

  return (
    <div className="space-y-6">
      {/* Deal Status Header */}
      {hasActiveDealStatuses && (
        <Card>
          <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-green-600 text-2xl">🎯</div>
                <div>
                  <h3 className="font-semibold text-gray-900">Live Odds Analysis Active</h3>
                  <p className="text-base tablet:text-sm text-gray-600">
                    Comparison showing real-time adjustments for {config.dealStatuses?.length || 0} game{config.dealStatuses?.length !== 1 ? 's' : ''} with custom jar fill levels
                  </p>
                </div>
              </div>
              <div className="text-right text-base tablet:text-sm text-gray-500">
                <div>Live metrics: <span className="text-green-600 font-medium">Green</span></div>
                {config.showOriginalOdds && <div>Original odds: <span className="text-gray-400 font-medium">Gray</span></div>}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Summary */}
      <MetricsSummaryCard results={results} />
      
      {/* Sort Controls */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2">
          <label className="text-base tablet:text-sm font-medium">Sort by:</label>
          <select
            value={config.sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-3 py-1 border border-gray-300 rounded text-base tablet:text-sm"
          >
            <option value="expectedValue">Expected Value</option>
            <option value="winProbability">Win Probability</option>
            <option value="holdPercentage">Hold Percentage</option>
            <option value="name">Name</option>
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <label className="text-base tablet:text-sm font-medium">Order:</label>
          <select
            value={config.sortOrder}
            onChange={(e) => setSortOrder(e.target.value as any)}
            className="px-3 py-1 border border-gray-300 rounded text-base tablet:text-sm"
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>
        
        {/* Show original odds checkbox - only when deal statuses are active */}
        {hasActiveDealStatuses && (
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-base tablet:text-sm">
              <input
                type="checkbox"
                checked={config.showOriginalOdds}
                onChange={(e) => setShowOriginalOdds(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Show Original Odds
            </label>
          </div>
        )}
        
        <Button
          onClick={() => setShowDetailedView(true)}
          variant="secondary"
          size="sm"
        >
          View Detailed Table
        </Button>
        
        <Button
          onClick={clearResults}
          variant="secondary"
          size="sm"
        >
          New Comparison
        </Button>
      </div>

      {/* Game Cards Grid */}
      <div className="grid tablet:grid-cols-2 desktop:grid-cols-3 gap-6">
        {results.games.map((game, index) => (
          <GameComparisonCard
            key={game.id}
            game={game}
            isFirst={index === 0}
          />
        ))}
      </div>

      {/* Detailed View Modal */}
      <Modal
        isOpen={showDetailedView}
        onClose={() => setShowDetailedView(false)}
        title="Detailed Game Comparison"
        size="xl"
      >
        <DetailedComparisonTable results={results} />
      </Modal>
    </div>
  );
};